import { mapCanBeReserved, mapCanBeSold, mapCanBeSubscribed, mapLabels, mapShade } from '@ori/product-box';

import type {
  ConceptFragment,
  GetProductsQuery,
  Product,
  ProductFragment,
  ProductWithReplacementsFragment,
  ReplacementFragment,
  ShadeFragment,
  Shade,
  SubscriptionBenefitBannerData,
  SubscriptionBenefitBannerFragment,
} from '../models';
import { isDeletedShade } from '../utils';

export const getProductSize = ({ volume, volumeUnit }: ShadeFragment): Shade['size'] => {
  if (volume != null && volumeUnit != null) {
    return `${volume} ${volumeUnit.trim()}`;
  }

  return null;
};

export const mapProductDiscontinuedAndCannotBeSold = (
  shades: Array<Partial<Shade> & Pick<Shade, 'canBeSold' | 'isDiscontinued'>>,
): boolean => {
  if (shades.length === 0) {
    return false;
  }

  return shades.every((shade) => shade.isDiscontinued && !shade.canBeSold);
};

export const mapSubscriptionBenefitBanner = (
  data: NonNullable<SubscriptionBenefitBannerFragment['subscriptionBenefitBanner']>,
): SubscriptionBenefitBannerData => ({
  title: data.title ?? '',
  message: data.message ?? '',
  readMoreText: data.readMoreLink?.title ?? '',
  readMoreUrl: data.readMoreLink?.relativeUrl ?? '',
  registerText: data.registerLink?.title ?? '',
  registerUrl: data.registerLink?.relativeUrl ?? '',
  signInText: data.signInLink?.title ?? '',
});

export const mapShades = (shades: ConceptFragment['products']): Shade[] =>
  (shades ?? [])
    .filter((shade): shade is NonNullable<ShadeFragment> => shade != null && !isDeletedShade(shade))
    .map((shade): Shade => {
      const { hexColors, imageUrl } = mapShade(shade);
      const { deal, ppa } = mapLabels(shade.labels) ?? {};

      return {
        canBeReserved: shade.availability?.isBackorder ?? mapCanBeReserved(shade.labels),
        canBeSold: mapCanBeSold(shade.labels),
        canBeSubscribed: mapCanBeSubscribed(shade.labels),
        isDiscontinued: shade.isDiscontinued ?? false,
        color: {
          name: shade.color ?? null,
          hexColors,
          imageUrl,
        },
        dealLabel: deal,
        imageUrl: shade.imageUrl ?? null,
        loyaltyRedeemPoints: shade.formattedPrice?.bp ?? 0,
        pdpUrl: shade.pdpUrl?.relativeUrl ?? null,
        ppaLabel: ppa,
        price: {
          basicCashback: shade.formattedPrice?.price?.basicCashback ?? null,
          current: shade.formattedPrice?.price?.currentPrice ?? null,
          old: shade.formattedPrice?.price?.basicCataloguePrice ?? null,
          previousMinOfferPrice: shade.formattedPrice?.price?.previousMinOfferPrice ?? null,
          sharingBonus: shade.formattedPrice?.price?.sharingBonus ?? null,
        },
        productCode: shade.productCode,
        size: getProductSize(shade),
        showBackInStockNotification: shade.backInStockAvailability?.showBackInStockNotification ?? false,
        subscriptionBenefitBanner: shade.subscriptionBenefitBanner
          ? mapSubscriptionBenefitBanner(shade.subscriptionBenefitBanner)
          : null,
      };
    });

export const mapProductBase = (product: ProductFragment | null | undefined): Product | null | undefined => {
  const shades = mapShades(product?.concept?.products);
  if (!product?.concept?.name || shades.length === 0) {
    return null;
  }

  return {
    brandName: product.concept.brand?.name ?? null,
    brandUrl: product.concept.brand?.url ?? null,
    code: product.productCode,
    description: product.concept.description ?? null,
    keyFeatures: product.concept.benefits?.filter((benefit): benefit is string => benefit != null) ?? [],
    name: product.concept.name,
    ratingAverage: product.concept.ratingAverage ?? 0,
    ratingCount: product.concept.ratingCount ?? 0,
    replacements: [],
    shades,
    isDiscontinuedAndCannotBeSold: mapProductDiscontinuedAndCannotBeSold(shades),
  };
};

export const mapProductReplacementFromGql = (replacement?: ReplacementFragment | null | undefined): Product | null => {
  const productBase = mapProductBase(replacement?.product);
  if (!productBase) {
    return null;
  }
  const { brandName, brandUrl, code, description, keyFeatures, name, ratingAverage, ratingCount, shades } = productBase;

  return {
    brandName,
    brandUrl,
    code,
    description,
    keyFeatures,
    name,
    ratingAverage,
    ratingCount,
    replacements: [],
    shades,
  };
};

export const mapProductReplacementsFromGql = ({
  replacements,
}: NonNullable<ProductWithReplacementsFragment>): Product[] =>
  (replacements ?? [])
    .map(mapProductReplacementFromGql)
    .filter((replacement): replacement is Product => replacement != null);

export const mapProductFromGql = (product: NonNullable<ProductWithReplacementsFragment>): Product | null => {
  const productBase = mapProductBase(product);
  if (!productBase) {
    return null;
  }
  const {
    brandName,
    brandUrl,
    code,
    description,
    keyFeatures,
    name,
    ratingAverage,
    ratingCount,
    shades,
    isDiscontinuedAndCannotBeSold,
  } = productBase;

  return {
    brandName,
    brandUrl,
    code,
    description,
    keyFeatures,
    name,
    ratingAverage,
    ratingCount,
    replacements: mapProductReplacementsFromGql(product),
    shades,
    isDiscontinuedAndCannotBeSold,
  };
};

export const mapProductsFromGql = (products: NonNullable<GetProductsQuery['products']>): Product[] =>
  products
    .map((product) => (product ? mapProductFromGql(product) : null))
    .filter((product): product is Product => product != null);
